﻿import { h, FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { useLocalized } from "./KPIVisualiser_Context";

export const DAYS_TO_MS = 1000 * 60 * 60 * 24;
export const MS_TO_DAYS = 1 / DAYS_TO_MS;
export const DAY_WIDTH = 128.0;

/** The margin between the top of the document and the top of the grid lines. */
export const GRID_MARGIN_TOP = 56.0;
/** Half of the spacing between KPI entries. */
export const ENTRY_PADDING = 6.0;
/** The height of a KPI entry. */
export const ENTRY_HEIGHT = 54.0;
/** The margin between the top of the document and the top of the first entry. */
export const ENTRY_MARGIN_TOP = GRID_MARGIN_TOP + ENTRY_PADDING + 6.0;

export const LEFT_HEADER_WIDTH = 256.0;

export const DAY_OF_WEEK_TO_NUM_MAP = {
    "sunday": 0,
    "monday": 1,
    "tuesday": 2,
    "wednesday": 3,
    "thursday": 4,
    "friday": 5,
    "saturday": 6,
}


export function dateToXPos(date: Date | number, zoom: number): number {
    if (typeof date === 'number') {
        date = new Date(date);
    }

    const curDate = new Date();
    const dateDiff = date.getTime() - curDate.getTime();

    return (DAY_WIDTH * zoom) * (dateDiff * MS_TO_DAYS);
}

export function xPosToDate(xPos: number, zoom: number): Date {
    const curDate = new Date();
    const targetDate = (xPos / (DAY_WIDTH * zoom)) * DAYS_TO_MS;

    return new Date(curDate.getTime() + targetDate);
}

export function dateObjToUtcDate(d: string | number | Date) {
    if (typeof d === "string") {
        const dLwr = d.toLowerCase();
        const hasOffset = dLwr.endsWith("z") || /[+-]\d\d:?\d\d/.test(dLwr);

        if (!hasOffset) {
            d += "z";
        }
    }

    return new Date(d);
}

export function utcDateToLocalDate(d: string | number | Date) {
    if (!(d instanceof Date)) {
        d = new Date(d);
    }

    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()));
}


export function localDateToUtcDate(d: string | number | Date) {
    if (!(d instanceof Date)) {
        d = new Date(d);
    }

    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds());
}

export function entryIndexToYPos(index: number, clamped?: boolean): number {
    const y = index * (ENTRY_HEIGHT + ENTRY_PADDING * 2) - ENTRY_PADDING;
    return clamped ? Math.max(y, 0) : y;
}

export function extractTimeFromDate(date: Date) {
    const hours = date.getHours() * 1000 * 60 * 60;
    const minutes = date.getMinutes() * 1000 * 60;
    const seconds = date.getSeconds() * 1000;
    const ms = date.getMilliseconds();

    return hours + minutes + seconds + ms;
}

export function getKpiStateLabel(startDate: Date, deadlineDate: Date, endDate: Date | null, isPrediction: boolean = false) {
    const finishedEarlyStr = useLocalized("lbl:finishedEarly", "Finished early");
    const finishedOnTimeStr = useLocalized("lbl:finishedOnTime", "Finished on time");
    const finishedLateStr = useLocalized("lbl:finishedLate", "Finished late");
    const predictedStartTimeStr = useLocalized("lbl:predictedStartTime", "Predicted start time");
    const inProgressStr = useLocalized("lbl:inProgress", "In progress");
    const runningLateStr = useLocalized("lbl:runningLate", "Running late");

    if (isPrediction) {
        return predictedStartTimeStr;
    }

    const now = new Date();
    let label;

    if (endDate != null && endDate < now) {
        if (endDate > deadlineDate) {
            label = finishedLateStr;
        } else {
            const dayDiff = Math.abs(endDate.getTime() - deadlineDate.getTime()) * MS_TO_DAYS;
            if (dayDiff > 1.0) {
                label = finishedEarlyStr;
            } else {
                label = finishedOnTimeStr;
            }
        }
    } else {
        //Only show the predicted label if the deadline is after the start date and KPI hasn't started
        if (startDate > now && deadlineDate > startDate) {
            debugger;
            label = predictedStartTimeStr;
        } else {
            label = inProgressStr;

            if (deadlineDate < now) {
                label += ` - ${runningLateStr}!`;
            }
        }
    }

    return label;
}

export const SvgCommonDefs: FunctionComponent = memo(() => {
    return (
        <defs>
            <pattern id="frozenHatch" width="10" height="10" patternTransform="rotate(45)" patternUnits="userSpaceOnUse" >
                <rect x="0" y="0" width="10" height="10" fill="rgb(0,156,166,0.2)" />
                <line x1="0" y1="0" x2="0" y2="10" stroke="rgb(0,156,166,0.4)" />
            </pattern>
            <pattern id="agreedDeadlinePreStartDateHatch" width="10" height="10" patternTransform="rotate(135)" patternUnits="userSpaceOnUse" >
                <rect x="0" y="0" width="10" height="10" fill="rgb(0,0,0,0.5)"/>
                <line x1="0" y1="0" x2="0" y2="10" stroke="rgb(0,0,0,0.5)" />
            </pattern>
            <pattern id="agreedDeadlinePreStartDateLateHatch" width="10" height="10" patternTransform="rotate(135)" patternUnits="userSpaceOnUse" >
                <rect x="0" y="0" width="10" height="10" fill="rgb(166,16,16,0.2)" />
                <line x1="0" y1="0" x2="0" y2="10" stroke="rgb(255,0,0,1)" />
            </pattern>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" id="kpi_type_contractual">
                <path d="M218.17 424.14c-2.95-5.92-8.09-6.52-10.17-6.52s-7.22.59-10.02 6.19l-7.67 15.34c-6.37 12.78-25.03 11.37-29.48-2.09L144 386.59l-10.61 31.88c-5.89 17.66-22.38 29.53-41 29.53H80c-8.84 0-16-7.16-16-16s7.16-16 16-16h12.39c4.83 0 9.11-3.08 10.64-7.66l18.19-54.64c3.3-9.81 12.44-16.41 22.78-16.41s19.48 6.59 22.77 16.41l13.88 41.64c19.75-16.19 54.06-9.7 66 14.16 1.89 3.78 5.49 5.95 9.36 6.26v-82.12l128-127.09V160H248c-13.2 0-24-10.8-24-24V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24v-40l-128-.11c-16.12-.31-30.58-9.28-37.83-23.75zM384 121.9c0-6.3-2.5-12.4-7-16.9L279.1 7c-4.5-4.5-10.6-7-17-7H256v128h128v-6.1zm-96 225.06V416h68.99l161.68-162.78-67.88-67.88L288 346.96zm280.54-179.63l-31.87-31.87c-9.94-9.94-26.07-9.94-36.01 0l-27.25 27.25 67.88 67.88 27.25-27.25c9.95-9.94 9.95-26.07 0-36.01z" />
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" id="kpi_type_agreed">
                <path d="M434.7 64h-85.9c-8 0-15.7 3-21.6 8.4l-98.3 90c-.1.1-.2.3-.3.4-16.6 15.6-16.3 40.5-2.1 56 12.7 13.9 39.4 17.6 56.1 2.7.1-.1.3-.1.4-.2l79.9-73.2c6.5-5.9 16.7-5.5 22.6 1 6 6.5 5.5 16.6-1 22.6l-26.1 23.9L504 313.8c2.9 2.4 5.5 5 7.9 7.7V128l-54.6-54.6c-5.9-6-14.1-9.4-22.6-9.4zM544 128.2v223.9c0 17.7 14.3 32 32 32h64V128.2h-96zm48 223.9c-8.8 0-16-7.2-16-16s7.2-16 16-16 16 7.2 16 16-7.2 16-16 16zM0 384h64c17.7 0 32-14.3 32-32V128.2H0V384zm48-63.9c8.8 0 16 7.2 16 16s-7.2 16-16 16-16-7.2-16-16c0-8.9 7.2-16 16-16zm435.9 18.6L334.6 217.5l-30 27.5c-29.7 27.1-75.2 24.5-101.7-4.4-26.9-29.4-24.8-74.9 4.4-101.7L289.1 64h-83.8c-8.5 0-16.6 3.4-22.6 9.4L128 128v223.9h18.3l90.5 81.9c27.4 22.3 67.7 18.1 90-9.3l.2-.2 17.9 15.5c15.9 13 39.4 10.5 52.3-5.4l31.4-38.6 5.4 4.4c13.7 11.1 33.9 9.1 45-4.7l9.5-11.7c11.2-13.8 9.1-33.9-4.6-45.1z" />
            </svg>
        </defs>
    );
});
